import type { NodeRenderer } from "@tento-ui/brand/page-engine.types";
import { LandingBento } from "@tento-ui/brand/landing/landing-bento";
import { LandingSection } from "@tento-ui/brand/landing/landing-section";
import { StickerBentoHero } from "@tento-ui/brand/landing/sticker-bento-hero";
import { StickerDetachButton } from "@tento-ui/brand/landing/sticker-detach-button";
import { StickerPopHandle } from "@tento-ui/brand/landing/landing-sticker-playground";

import type { PageEngineNodeProps } from "./to-props";

// A `byId` section override (see escape-hatches.md): the `landing.stickers` node
// is a generic `feature-grid` for SSR (heading + first paint), but the client
// renders a bespoke single-item bento whose hero slot hosts the interactive
// sticker playground — UI that escapes the design-system section dictionary.

const STICKER_SECTION_BACKGROUND = "var(--bg)";

function readString(
  props: PageEngineNodeProps,
  key: string,
  fallback: string,
): string {
  const value = props.node.props[key];
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

function makeStickerBento(props: PageEngineNodeProps): HTMLElement {
  // Shared between the visual (the playground) and the accessory (the detach
  // button): the playground registers its pop-out on this handle and the button
  // triggers it. One handle per render keeps the two in-sync across the card's
  // text-column / media-slot shadow boundary.
  const popHandle = new StickerPopHandle();
  return LandingBento.makeWith({
    title: readString(props, "heading", "Make the week yours"),
    items: [
      {
        title: "Stickers",
        description: readString(
          props,
          "subtext",
          "Mark travel, launches, deadlines, and recovery so the days that matter stand out at a glance.",
        ),
        span: 3,
        // Text left, tiled sticker grid in the right hero slot (like the other
        // stretched hero cards), with matching feature-card text treatment.
        imagePosition: "right",
        cardClassName: "feature-card",
        // Text-left / visual-right is a flex row, so the card height is driven by
        // the (short) text column and the sticker grid would collapse to a sliver.
        // This is the deliberate card/clip height the grid bleeds out the bottom
        // of — same role as the other hero cards' fixed visual height.
        visualHeight: 360,
        // The "→ in a circle" detach control under the copy — pops the sheet out
        // to play with; flips to an X (close) while popped, and toggling it (or
        // closing the popped sheet any other way) flips it back via popHandle.
        renderAccessory: () => {
          const $detach = StickerDetachButton.makeWith({
            detachLabel: "Pop out the sticker sheet to play",
            closeLabel: "Close the sticker sheet",
            onClick: () => popHandle.toggle(),
          });
          popHandle.onStateChange((popped) => $detach.setPopped(popped));
          return $detach;
        },
        // The bento hero: a separate size-aware gradient wash layer behind the
        // interactive sticker playground — so the sheet scales independently
        // while the wash regenerates for the hero slot.
        renderVisual: () => StickerBentoHero.makeWith({ popHandle }),
      },
    ],
  });
}

function makeStickerSection(props: PageEngineNodeProps): HTMLElement {
  const $section = LandingSection.makeWith({
    paddingY: 56,
    background: STICKER_SECTION_BACKGROUND,
    gap: 0,
    border: true,
    first: false,
    maxWidth: null,
  });
  $section.appendChild(makeStickerBento(props));
  return $section;
}

export const stickerSheetRenderer: NodeRenderer<PageEngineNodeProps> = {
  make(props: PageEngineNodeProps): HTMLElement {
    return makeStickerSection(props);
  },
  bind(_element: HTMLElement, props: PageEngineNodeProps): HTMLElement {
    return makeStickerSection(props);
  },
  skeleton: null,
};
